import React, { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CaseStudySummary } from './components/CaseStudySummary';
import { PageContext } from '../builder';
import { MarketInsightsSummary } from './components/MarketInsightsSummary';
import { BusinessGuideSummary } from './components/BusinessGuideSummary';
import { useIsPreviewing } from '@builder.io/react';
import { builder, BuilderContent } from '@builder.io/sdk';

interface ArticleSummaryProps {
  summaryType?: "market-insights" | "case-studies" | "business-guides";
  caseStudies?: {
    companyProfile?: string;
    challenge?: string;
    showIndustry?: boolean;
    companySize?: string;
    showProducts?: boolean;
  }
  marketInsights?: {
    showDate?: boolean;
    overrideDate?: boolean;
    Date?: string;
    showAuthor?: boolean;
    showReviewer?: boolean;
    showTopics?: boolean;
  }
  businessGuides?: {
    showDate?: boolean;
    overrideDate?: boolean;
    Date?: string;
    showAuthor?: boolean; 
    showReviewer?: boolean;
    showTopics?: boolean;
  }
  attributes?: React.HTMLProps<HTMLElement>;
}

export const ArticleSummary = ({ 
    summaryType,
    caseStudies,
    marketInsights,
    businessGuides,
    attributes,
}: ArticleSummaryProps) => {
  const { key, ...rest } = attributes ?? {};
  const sectionClass = cn(
    'cta-article-summary',
  );
  const [isCopied, setIsCopied] = useState(false);
  const isPreviewing = useIsPreviewing();
  const [currentPageContent, setCurrentPageContent] = useState<BuilderContent>();
 
  const handleShareViaEmail = () => {
    const firstH1 = document.querySelector('h1');
    const subject = firstH1?.textContent?.trim() || 'Shared Article | Octet';
    const body = window.location.href;
  
    const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
  };

  const handleShareViaLinkedIn = () => {
    const url = encodeURIComponent(window.location.href);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
  };
  const copyUrl = () => {
    navigator.clipboard.writeText(window.location.href);
    setIsCopied(true);
  };

  useEffect(() => {
    if (isCopied) {
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    }
  }, [isCopied]);

  const pageFields = React.useContext(PageContext);

  // Get current page content directly from Builder.io
  useEffect(() => {
    if (isPreviewing) {
      const fetchCurrentContent = async () => {
        try {
          const currentPath = window.location.pathname;
          
          const content = await builder.get('market-insights', {
            userAttributes: {
              urlPath: currentPath,
            },
            includeUnpublished: true,
            noEditorUpdates: false,
            enrich: true,
            includeRefs: true,
          }).toPromise();
          
          if (content) {
            setCurrentPageContent(content.data);
          }
        } catch (error) {
          console.error('Error fetching current content:', error);
        }
      };

      // Fetch immediately and then periodically
      fetchCurrentContent();
      const interval = setInterval(fetchCurrentContent, 2000);
      
      return () => clearInterval(interval);
    }
  }, [isPreviewing]);

  // Use current page content if available, otherwise fall back to pageFields
  const dataToUse = currentPageContent || pageFields?.data;

  const marketInsightsDate = marketInsights?.overrideDate ? marketInsights?.Date : (pageFields?.data as unknown as { date?: string })?.date;
  const businessGuidesDate = businessGuides?.overrideDate ? businessGuides?.Date : (pageFields?.data as unknown as { date?: string })?.date;

  const marketInsightsData = {
    date: marketInsightsDate,
    author: (dataToUse as unknown as { author?: string })?.author,
    reviewer: (dataToUse as unknown as { reviewer?: string })?.reviewer,
    topics: (dataToUse as unknown as { topics?: unknown[] })?.topics,
  };
  const businessGuidesData = {
    date: businessGuidesDate,
    author: (dataToUse as unknown as { author?: string })?.author,
    type: (dataToUse as unknown as { type?: string })?.type,
  };

  return (
    <section
      key={key}
      {...rest}
      className={sectionClass}
    >
      {summaryType === "case-studies" && caseStudies && (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        <CaseStudySummary summary={caseStudies} data={dataToUse as any}/>
      )}
      {summaryType === "market-insights" && marketInsights && (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        <MarketInsightsSummary summary={marketInsights} data={marketInsightsData as any}/>
      )}  
      {summaryType === "business-guides" && businessGuides && (
        <BusinessGuideSummary summary={businessGuides} data={businessGuidesData}/>
      )}  

      <div className="flex gap-2 justify-between mt-6 lg:mt-10">
        <TooltipProvider>
          <Tooltip open={isCopied}>
            <TooltipTrigger asChild>
              <Button 
                variant="secondary" 
                size="sm"
                className='flex items-center gap-1 cursor-pointer'
                onClick={copyUrl}
              >
                <span className="material-symbols-outlined font-bold">
                  content_copy
                </span>
                Copy URL
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <span className='paragraph-sm'>URL Copied</span>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="iconSm"
            grayBorder
            className='border-1'
            onClick={handleShareViaEmail}
          >
            <span className="material-symbols-outlined font-bold">
              mail
            </span>
          </Button>
          <Button 
            variant="outline" 
            size="iconSm" 
            grayBorder
            className='border-1'
            onClick={handleShareViaLinkedIn}
          >
            <svg className='size-6' width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g id="Frame 58">
              <path id="Vector" d="M7.59202 20.5H4.26689V9.80708H7.59202V20.5ZM5.92945 8.35015C4.86191 8.35015 4 7.48824 4 6.42508C4 5.36191 4.86191 4.5 5.92945 4.5C6.99262 4.5 7.85453 5.36191 7.85453 6.42508C7.85453 7.48387 6.99262 8.35015 5.92945 8.35015ZM20.035 20.5H16.7142V15.3023C16.7142 14.0641 16.6924 12.4672 14.9861 12.4672C13.2579 12.4672 12.9954 13.8191 12.9954 15.2148V20.5H9.67897V9.80708H12.8641V11.2684H12.9078C13.3497 10.4284 14.4348 9.5402 16.0492 9.5402C19.4137 9.5402 20.035 11.754 20.035 14.6329V20.5Z" fill="var(--primary)"/>
              </g>
            </svg>
          </Button>
        </div>
      </div>
    </section>
  );
}; 
